import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUserEvent } from '../hooks';
import { ThemeContext } from '../App';
import { FaArrowRight, FaBars, FaBell, FaCheckCircle, FaClipboardCheck, FaCloudSun, FaClock, FaCrown, FaExclamationTriangle, FaMicrochip, FaMoon, FaSignOutAlt, FaSun, FaTree, FaUser } from 'react-icons/fa';
import { supabase } from '../supabaseClient';
import Notificacoes from './Notificacoes';
import { usePlanLimits } from '../hooks/usePlanLimits';
import PlanBadge from '../components/PlanBadge';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { cadernoRuntime, listCadernoNotifications, listCadernoOrders, listIrrigationNotifications } from '../services/cadernoService';
import { summarizeFieldTasks } from '../utils/fieldTaskSummary';
import { getDashboardDataState } from '../utils/dashboardDataState';
import { isModuleAllowed } from '../config/modulePermissions';
import { isActiveCrop } from '../utils/cropLifecycle';
import { deduplicateOperationalNotifications } from '../services/operationalNotificationService';
import { loadVisibleAuthorizedProperties } from '../services/authorizedPropertiesService';
import './Dashboard.css';

const DB_NOTIFICATION_TYPE_TO_PRIORITY = {
  urgente: 'urgent',
  alta: 'high',
  normal: 'normal',
  baixa: 'low',
};

function formatNotificationTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Agora';
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 60) return minutes < 1 ? 'Agora' : `Há ${minutes} min`;
  if (minutes < 1440) return `Há ${Math.floor(minutes / 60)} h`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

export default function Dashboard({
  onSelecionarTela,
  telaSelecionada,
  user,
  onLogout,
  onOpenAlerts = null,
  onOpenFieldNotebook = null,
  previewInsights = null,
  allowedModules = [],
  isAdmin = false,
  integratedNavigation = false,
}) {
  // Registra page view e duração de sessão no Dashboard
  useUserEvent({ userId: user?.id, tela: 'Dashboard', evento: 'page_view', comDuração: true });
  const { darkMode, setDarkMode } = useContext(ThemeContext);
  const [menuAberto, setMenuAberto] = useState(false);
  const [menuTop, setMenuTop] = useState(72);
  const headerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [operationalNotifications, setOperationalNotifications] = useState([]);
  const [operationalNotificationsLoading, setOperationalNotificationsLoading] = useState(false);
  const [operationalNotificationsReady, setOperationalNotificationsReady] = useState(Boolean(previewInsights));
  const [operationalNotificationsError, setOperationalNotificationsError] = useState(false);
  const [fieldTaskSummary, setFieldTaskSummary] = useState({
    loading: true,
    error: false,
    today: 0,
    late: 0,
    approval: 0,
    actionable: 0,
  });
  const [fieldTaskSummaryReady, setFieldTaskSummaryReady] = useState(Boolean(previewInsights));
  const [heroInsights, setHeroInsights] = useState(previewInsights || {
    loading: true,
    activeAlerts: 0,
    lastSyncAt: null,
    propertyCount: 0,
    sensorCount: 0,
    activeTalhoesCount: 0,
    errors: {},
  });
  const allowedModuleSet = useMemo(() => new Set(allowedModules || []), [allowedModules]);
  const hasAdminAccess = Boolean(isAdmin || user?.is_admin);
  const canAccessModule = moduleKey => (
    isModuleAllowed(moduleKey, allowedModuleSet, hasAdminAccess, user?.plano)
  );
  const canViewAlerts = canAccessModule('alertas');
  const canViewNotifications = canAccessModule('notificacoes-operacionais');
  const canViewFieldNotebook = canAccessModule('caderno');
  const canViewProperties = canAccessModule('propriedades');
  const canViewForecast = canAccessModule('previsao');
  const canViewSensors = canAccessModule('sensores');
  const canViewOperations = canAccessModule('sensoriamento');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const updateMenuTop = () => {
      if (!headerRef.current) return;
      const { bottom } = headerRef.current.getBoundingClientRect();
      setMenuTop(Math.max(56, Math.round(bottom)));
    };
    updateMenuTop();
    window.addEventListener('resize', updateMenuTop);
    return () => window.removeEventListener('resize', updateMenuTop);
  }, []);

  // Sistema de Planos
  const { plano } = usePlanLimits(user?.id);

  // Notificação de vencimento do plano
  useEffect(() => {
    if (!user?.id || !plano?.dataExpiracao || plano?.tipo === 'starter') return;
    const exp = new Date(plano.dataExpiracao);
    const hoje = new Date();
    const diasRestantes = Math.ceil((exp - hoje) / 86400000);
    if (diasRestantes > 7) return;

    async function verificarEEnviar() {
      try {
        // Evita duplicata: não envia se já existe notificação deste tipo nas últimas 23h
        const limite = new Date(Date.now() - 23 * 60 * 60 * 1000).toISOString();
        const { data: existente } = await supabase
          .from('notificacoes')
          .select('id')
          .eq('user_id', user.id)
          .eq('tipo', 'vencimento_plano')
          .gte('criado_em', limite)
          .limit(1);
        if (existente && existente.length > 0) return;

        const nomePlano = plano.tipo.charAt(0).toUpperCase() + plano.tipo.slice(1);
        const dataFmt = exp.toLocaleDateString('pt-BR');
        const titulo = diasRestantes <= 0
          ? `Plano ${nomePlano} expirado`
          : diasRestantes === 1
          ? `Seu plano ${nomePlano} vence amanhã!`
          : `Seu plano ${nomePlano} vence em ${diasRestantes} dias`;
        const mensagem = diasRestantes <= 0
          ? 'Seu plano expirou. Renove agora para não perder o acesso aos recursos.'
          : `Plano válido até ${dataFmt}. Renove para garantir a continuidade.`;

        await supabase.from('notificacoes').insert([{
          user_id: user.id,
          titulo,
          mensagem,
          tipo: 'vencimento_plano',
          lida: false,
          criado_em: new Date().toISOString(),
          dados: { plano: plano.tipo, expiracao: plano.dataExpiracao, dias_restantes: diasRestantes }
        }]);
      } catch (e) { /* silencioso */ }
    }
    verificarEEnviar();
  }, [user?.id, plano?.tipo, plano?.dataExpiracao]);

  useEffect(() => {
    if (!user?.id || !canViewNotifications || !cadernoRuntime.readsEnabled) {
      setOperationalNotifications([]);
      setOperationalNotificationsLoading(false);
      setOperationalNotificationsError(false);
      setOperationalNotificationsReady(true);
      return undefined;
    }

    let cancelled = false;
    setOperationalNotificationsReady(false);
    async function loadRealNotifications() {
      if (!cancelled) {
        setOperationalNotificationsLoading(true);
        setOperationalNotificationsError(false);
      }
      try {
        const [cadernoResult, irrigationResult, visiblePropertiesResult] = await Promise.allSettled([
          listCadernoNotifications(),
          listIrrigationNotifications(),
          loadVisibleAuthorizedProperties(user.id, 'id'),
        ]);
        const allRows = cadernoResult.status === 'fulfilled' ? cadernoResult.value : [];
        const allIrrigationRows = irrigationResult.status === 'fulfilled' ? irrigationResult.value : [];
        if (cadernoResult.status === 'rejected' && irrigationResult.status === 'rejected') {
          throw cadernoResult.reason;
        }
        if (cancelled) return;

        // Propriedades excluídas (arquivadas) não devem gerar pendências no painel.
        const visibleProperties = visiblePropertiesResult.status === 'fulfilled'
          ? visiblePropertiesResult.value.properties
          : null;
        const visiblePropertyIds = visibleProperties
          ? new Set(visibleProperties.map(property => String(property.id)))
          : null;
        const rows = visiblePropertyIds
          ? allRows.filter(row => !row.propriedade_id || visiblePropertyIds.has(String(row.propriedade_id)))
          : allRows;
        const irrigationRows = visiblePropertyIds
          ? allIrrigationRows.filter(row => !row.dados?.propriedade_id || visiblePropertyIds.has(String(row.dados.propriedade_id)))
          : allIrrigationRows;

        const propertyIds = [...new Set(rows.map(row => row.propriedade_id).filter(Boolean))];
        const plotIds = [...new Set(rows.map(row => row.talhao_id).filter(Boolean))];
        const [propsRes, plotsRes] = await Promise.all([
          propertyIds.length > 0
            ? supabase.from('propriedades').select('id, nome').in('id', propertyIds)
            : Promise.resolve({ data: [] }),
          plotIds.length > 0
            ? supabase.from('talhoes').select('id, nome').in('id', plotIds)
            : Promise.resolve({ data: [] }),
        ]);
        if (cancelled) return;

        const propertyNameById = new Map((propsRes.data || []).map(item => [String(item.id), item.nome]));
        const plotNameById = new Map((plotsRes.data || []).map(item => [String(item.id), item.nome]));

        setOperationalNotifications(deduplicateOperationalNotifications([
          ...rows.map(row => ({
            id: row.id,
            title: row.titulo,
            message: row.mensagem,
            priority: DB_NOTIFICATION_TYPE_TO_PRIORITY[row.prioridade] || 'normal',
            propertyName: propertyNameById.get(String(row.propriedade_id)) || 'Propriedade',
            plotName: row.talhao_id == null ? 'Toda a propriedade' : (plotNameById.get(String(row.talhao_id)) || 'Talhão'),
            orderId: row.ordem_id,
            orderCode: row.dados?.codigo || null,
            createdAt: row.criada_em,
            readAt: row.lida_em,
          })),
          ...irrigationRows.map(row => ({
            id: row.id,
            title: row.titulo,
            message: row.mensagem,
            priority: row.dados?.prioridade === 'urgente' ? 'urgent' : row.dados?.prioridade === 'alta' ? 'high' : 'normal',
            propertyName: row.dados?.propriedade_nome || 'Propriedade',
            plotName: row.dados?.talhao_nome || 'Talhão',
            createdAt: row.criado_em,
            readAt: row.lida ? row.criado_em : null,
          })),
        ]).sort((first, second) => new Date(second.createdAt) - new Date(first.createdAt)));
        setOperationalNotificationsError(false);
      } catch (_) {
        if (!cancelled) {
          setOperationalNotifications([]);
          setOperationalNotificationsError(true);
        }
      } finally {
        if (!cancelled) {
          setOperationalNotificationsLoading(false);
          setOperationalNotificationsReady(true);
        }
      }
    }

    loadRealNotifications();
    const interval = setInterval(loadRealNotifications, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.id, canViewNotifications]);

  useEffect(() => {
    if (!user?.id || !canViewFieldNotebook || !cadernoRuntime.readsEnabled) {
      setFieldTaskSummary({ loading: false, error: false, today: 0, late: 0, approval: 0, actionable: 0 });
      setFieldTaskSummaryReady(true);
      return undefined;
    }

    let cancelled = false;
    setFieldTaskSummaryReady(false);
    async function loadFieldTaskSummary() {
      if (!cancelled) setFieldTaskSummary(current => ({ ...current, loading: true, error: false }));
      try {
        const orders = await listCadernoOrders();
        if (!cancelled) setFieldTaskSummary({ ...summarizeFieldTasks(orders), loading: false, error: false });
      } catch (_) {
        if (!cancelled) setFieldTaskSummary({ loading: false, error: true, today: 0, late: 0, approval: 0, actionable: 0 });
      } finally {
        if (!cancelled) setFieldTaskSummaryReady(true);
      }
    }

    loadFieldTaskSummary();
    const interval = setInterval(loadFieldTaskSummary, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.id, canViewFieldNotebook]);

  // Botão voltar do Android: sai do app quando estiver no Dashboard
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handler;
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (!canGoBack) CapacitorApp.exitApp();
    }).then(h => { handler = h; });
    return () => { handler?.remove(); };
  }, []);

  useEffect(() => {
    if (previewInsights) return;

    let cancelled = false;

    if (user?.id) {
      setHeroInsights(current => ({ ...current, loading: true }));
    }

    async function fetchHeroInsights() {
      if (!user?.id) return;

      try {
        const [activeAlertsRes, propriedadeRes, sensoresRes] = await Promise.all([
          canViewAlerts ? supabase
            .from('alertas')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .in('fonte', ['sensor', 'previsao']) : Promise.resolve({ data: [], count: 0, error: null }),
          canViewProperties
            ? loadVisibleAuthorizedProperties(user.id, 'id, nome, municipio, estado, area')
            : Promise.resolve({ properties: [], error: null }),
          canViewSensors ? supabase
            .from('sensores_usuarios')
            .select('device_id, tipo_sensor')
            .eq('user_id', user.id)
            .not('device_id', 'is', null) : Promise.resolve({ data: [], error: null })
        ]);

        const propriedades = propriedadeRes?.properties || [];
        let propertiesError = Boolean(propriedadeRes?.error);

        let activeTalhoesCount = propriedades
          .filter(property => property._usesPhysicalPlots)
          .flatMap(property => property._operationalPlots || [])
          .filter(plot => plot.ciclo_atual?.status === 'ativo').length;
        const legacyPropertyIds = propriedades
          .filter(property => !property._usesPhysicalPlots)
          .map(property => property.id)
          .filter(Boolean);
        if (!propertiesError && legacyPropertyIds.length > 0) {
          const { data: talhoesData, error: talhoesError } = await supabase
            .from('talhoes')
            .select('id, data_plantio, data_colheita, propriedade_id')
            .in('propriedade_id', legacyPropertyIds);

          propertiesError = Boolean(talhoesError);

          activeTalhoesCount += (talhoesData || []).filter(t => isActiveCrop({
            ...t,
            cultura: t.cultura || 'Cultivo informado',
          })).length;
        }

        let lastSyncAt = null;
        const sensores = sensoresRes?.data || [];

        if (sensores.length > 0) {
          const idsMet = sensores
            .filter(s => (s.tipo_sensor || 'meteorologico') === 'meteorologico')
            .map(s => s.device_id);
          const idsSolo = sensores
            .filter(s => s.tipo_sensor === 'umidade_solo')
            .map(s => s.device_id);
          const idsChuva = sensores
            .filter(s => s.tipo_sensor === 'chuva_presenca')
            .map(s => s.device_id);

          const [metRes, soloRes, chuvaRes] = await Promise.all([
            idsMet.length > 0
              ? supabase
                  .from('dados_sensores_meteorologicos')
                  .select('timestamp')
                  .in('device_id', idsMet)
                  .order('timestamp', { ascending: false })
                  .limit(1)
              : Promise.resolve({ data: [] }),
            idsSolo.length > 0
              ? supabase
                  .from('dados_sensores_solo')
                  .select('timestamp')
                  .in('device_id', idsSolo)
                  .order('timestamp', { ascending: false })
                  .limit(1)
              : Promise.resolve({ data: [] }),
            idsChuva.length > 0
              ? supabase
                  .from('dados_sensores_chuva')
                  .select('timestamp')
                  .in('device_id', idsChuva)
                  .order('timestamp', { ascending: false })
                  .limit(1)
              : Promise.resolve({ data: [] })
          ]);

          const timestamps = [
            metRes?.data?.[0]?.timestamp,
            soloRes?.data?.[0]?.timestamp,
            chuvaRes?.data?.[0]?.timestamp,
          ].filter(Boolean);

          if (timestamps.length > 0) {
            timestamps.sort((a, b) => new Date(b) - new Date(a));
            lastSyncAt = timestamps[0];
          }
        }

        if (!cancelled) {
          setHeroInsights({
            loading: false,
            activeAlerts: activeAlertsRes?.count || 0,
            lastSyncAt,
            propertyCount: propriedades.length,
            sensorCount: sensores.length,
            activeTalhoesCount,
            errors: {
              alerts: Boolean(activeAlertsRes?.error),
              properties: propertiesError,
              sensors: Boolean(sensoresRes?.error),
            },
          });
        }
      } catch (_) {
        if (!cancelled) {
          setHeroInsights(prev => ({
            ...prev,
            loading: false,
            errors: {
              alerts: canViewAlerts,
              properties: canViewProperties,
              sensors: canViewSensors,
            },
          }));
        }
      }
    }

    fetchHeroInsights();
    const interval = setInterval(fetchHeroInsights, 120000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user?.id, previewInsights, canViewAlerts, canViewProperties, canViewSensors]);

  const nomeExibicao = user?.nome?.split(' ')[0] || user?.email || 'Perfil';

  const saudacao = (() => {
    const hora = new Date().getHours();
    if (hora < 12) return 'Bom dia';
    if (hora < 18) return 'Boa tarde';
    return 'Boa noite';
  })();

  const syncLabel = (() => {
    if (!heroInsights.lastSyncAt) return 'sem leituras recentes';
    const diffMs = Date.now() - new Date(heroInsights.lastSyncAt).getTime();
    const diffMin = Math.max(1, Math.floor(diffMs / 60000));
    if (diffMin < 60) return `${diffMin} min atrás`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h atrás`;
    return new Date(heroInsights.lastSyncAt).toLocaleDateString('pt-BR');
  })();

  const showGettingStarted = canViewProperties
    && !heroInsights.loading
    && !heroInsights.errors?.properties
    && heroInsights.propertyCount === 0;
  const unreadOperationalNotifications = useMemo(
    () => operationalNotifications.filter(notification => !notification.readAt),
    [operationalNotifications]
  );
  const dashboardOperationalItems = operationalNotifications.slice(0, 3);
  const realOperationalDataAvailable = canViewNotifications && cadernoRuntime.readsEnabled;
  const dashboardDataState = getDashboardDataState({
    hasUser: Boolean(user?.id),
    insightsLoading: heroInsights.loading,
    notificationsRequired: realOperationalDataAvailable,
    notificationsReady: operationalNotificationsReady,
    fieldTasksRequired: canViewFieldNotebook && cadernoRuntime.readsEnabled,
    fieldTasksReady: fieldTaskSummaryReady,
    propertyCount: heroInsights.propertyCount,
  });

  if (dashboardDataState === 'loading') {
    return (
      <div className={`dashboard-shell ${darkMode ? 'is-dark' : ''}`}>
        <div className="dashboard-shell-decoration" />
        <main className="dashboard-main dashboard-initial-loading" role="status" aria-live="polite">
          <section className="dashboard-loading-panel">
            <span className="dashboard-loading-kicker">Dados reais da conta</span>
            <h1>Carregando seus dados</h1>
            <p>Estamos reunindo propriedades, sensores e atividades antes de montar o seu painel.</p>
            <div className="dashboard-loading-bars" aria-hidden="true"><span /><span /><span /></div>
          </section>
        </main>
      </div>
    );
  }

  const readinessCards = [
    {
      key: 'risks',
      icon: <FaExclamationTriangle />,
      label: 'Riscos e alertas',
      value: heroInsights.loading ? '...' : (heroInsights.errors?.alerts ? '—' : heroInsights.activeAlerts),
      detail: heroInsights.loading
        ? 'Carregando dados reais da conta'
        : heroInsights.errors?.alerts
        ? 'Não foi possível carregar os alertas'
        : (heroInsights.activeAlerts > 0 ? 'Ocorrências configuradas para acompanhamento' : 'Nenhum alerta configurado no momento'),
      action: onOpenAlerts || (() => onSelecionarTela('alertas')),
      tone: heroInsights.loading ? 'neutral' : heroInsights.activeAlerts > 0 ? 'attention' : 'ok',
      visible: canViewAlerts,
    },
    {
      key: 'tasks',
      icon: <FaClipboardCheck />,
      label: 'Trabalho de campo',
      value: fieldTaskSummary.loading ? '...' : (fieldTaskSummary.error ? '—' : fieldTaskSummary.actionable),
      detail: fieldTaskSummary.error
        ? 'Não foi possível carregar as atividades'
        : fieldTaskSummary.actionable > 0
          ? `${fieldTaskSummary.late} atrasada(s) · ${fieldTaskSummary.today} para hoje`
          : 'Nenhuma atividade pendente para hoje',
      action: () => onOpenFieldNotebook ? onOpenFieldNotebook() : onSelecionarTela('caderno'),
      tone: fieldTaskSummary.late > 0 ? 'attention' : 'ok',
      visible: canViewFieldNotebook && cadernoRuntime.readsEnabled,
    },
    {
      key: 'sensors',
      icon: <FaMicrochip />,
      label: 'Estações e sensores',
      value: heroInsights.loading ? '...' : (heroInsights.errors?.sensors ? '—' : heroInsights.sensorCount),
      detail: heroInsights.loading
        ? 'Carregando dados reais da conta'
        : heroInsights.errors?.sensors
        ? 'Não foi possível carregar os sensores'
        : `Última sincronização: ${syncLabel}`,
      action: () => onSelecionarTela('sensores'),
      tone: heroInsights.sensorCount > 0 ? 'online' : 'neutral',
      visible: canViewSensors,
    },
    {
      key: 'properties',
      icon: <FaTree />,
      label: 'Propriedades e talhões',
      value: heroInsights.loading ? '...' : (heroInsights.errors?.properties ? '—' : heroInsights.propertyCount),
      detail: heroInsights.loading
        ? 'Carregando dados reais da conta'
        : heroInsights.errors?.properties
        ? 'Não foi possível carregar as propriedades'
        : `${heroInsights.activeTalhoesCount} talhão(ões) ativo(s)`,
      action: () => onSelecionarTela('propriedades'),
      tone: 'neutral',
      visible: canViewProperties,
    },
  ].filter(card => card.visible);

  const gettingStartedItems = [
    {
      key: 'primeira-propriedade',
      icon: <FaTree />,
      eyebrow: 'Recomendado',
      title: 'Cadastre sua primeira propriedade',
      description: 'Localize a fazenda e desenhe seu perímetro diretamente no mapa da Central de Operações.',
      action: 'Abrir Central de Operações',
      onClick: () => onSelecionarTela('sensoriamento'),
      accent: 'property',
      visible: canViewOperations,
    },
    {
      key: 'consultar-previsao',
      icon: <FaCloudSun />,
      eyebrow: 'Disponível agora',
      title: 'Consulte o clima da sua região',
      description: 'Veja chuva, temperatura, umidade, vento e ET₀ antes de planejar as atividades.',
      action: 'Ver previsão',
      onClick: () => onSelecionarTela('previsao'),
      accent: 'forecast',
      visible: canViewForecast,
    },
    {
      key: 'acompanhar-sensores',
      icon: <FaMicrochip />,
      eyebrow: heroInsights.sensorCount > 0
        ? `${heroInsights.sensorCount} ${heroInsights.sensorCount === 1 ? 'sensor conectado' : 'sensores conectados'}`
        : 'Quando precisar',
      title: heroInsights.sensorCount > 0 ? 'Acompanhe suas estações' : 'Conecte uma estação',
      description: heroInsights.sensorCount > 0
        ? 'Suas estações já estão enviando dados. Abra o painel para acompanhar as leituras.'
        : 'Adicione uma estação ClimoraX e acompanhe as condições reais diretamente do campo.',
      action: heroInsights.sensorCount > 0 ? 'Abrir sensores' : 'Conhecer sensores',
      onClick: () => onSelecionarTela('sensores'),
      accent: 'sensor',
      completed: heroInsights.sensorCount > 0,
      visible: canViewSensors,
    },
  ].filter(item => item.visible);

  return (
    <div className={`dashboard-shell ${darkMode ? 'is-dark' : ''}`}>
      <div className="dashboard-shell-decoration" />

      {!integratedNavigation && <header
        ref={headerRef}
        className="dashboard-header"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.25rem)',
          paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 1rem)',
          paddingRight: 'calc(env(safe-area-inset-right, 0px) + 1rem)'
        }}
      >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            minWidth: 0, 
            flex: 1
          }}>
            <img 
              src="/logo.png" 
              alt="ClimoraX - Clima para o Agro" 
              className="dashboard-logo"
              style={{ 
                height: 'clamp(56px, 15vw, 84px)',
                width: 'auto',
                maxWidth: '340px',
                objectFit: 'contain',
                display: 'block'
              }} 
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Badge do Plano */}
            {plano && (
              <div 
                onClick={() => onSelecionarTela('perfil')}
                style={{ 
                  cursor: 'pointer',
                  flexShrink: 0,
                  display: isMobile ? 'none' : 'block'
                }}
                title="Clique para ver detalhes do plano"
              >
                <PlanBadge plano={plano.tipo} size="small" />
              </div>
            )}
            
            <Notificacoes 
              user={user} 
              onPropriedadeClick={() => window.location.reload()}
            />
            
            <button
              onClick={() => setMenuAberto(!menuAberto)}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                fontSize: 24,
                cursor: 'pointer',
                padding: '0.5rem',
                minWidth: 44,
                minHeight: 44,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
              title="Menu"
              aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuAberto}
            >
              <FaBars />
            </button>
          </div>
          
          {menuAberto && (
              <>
                {/* Backdrop para fechar menu */}
                <div
                  onClick={() => setMenuAberto(false)}
                  style={{
                    position: 'fixed',
                    top: menuTop,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.3)',
                    zIndex: 99
                  }}
                />
                <div style={{
                  position: 'fixed',
                  top: menuTop + 8,
                  right: '1rem',
                  background: darkMode ? '#1e1e1e' : '#fff',
                  borderRadius: 16,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
                  padding: '0.6rem 0',
                  zIndex: 101,
                  minWidth: 220,
                  overflow: 'hidden'
                }}>

                  {/* Cabeçalho: nome + email */}
                  <div style={{
                    padding: '0.6rem 1.1rem 0.8rem 1.1rem',
                    borderBottom: `1px solid ${darkMode ? '#2e2e2e' : '#f0f0f0'}`
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: darkMode ? '#fff' : '#1a1a1a' }}>
                      {nomeExibicao}
                    </div>
                    {user?.email && (
                      <div style={{ fontSize: 12, color: darkMode ? '#888' : '#999', marginTop: 2 }}>
                        {user.email}
                      </div>
                    )}
                  </div>

                  {/* Grupo: Conta */}
                  <div style={{ padding: '0.4rem 0' }}>
                    <button
                      onClick={() => { setMenuAberto(false); onSelecionarTela('perfil'); }}
                      style={{
                        width: '100%', background: 'none', border: 'none',
                        color: darkMode ? '#90caf9' : '#1976d2',
                        fontWeight: 600, fontSize: 15,
                        padding: '0.55rem 1.1rem',
                        textAlign: 'left', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 10
                      }}
                    >
                      <FaUser size={14} /> Perfil
                    </button>
                    <button
                      onClick={() => { setMenuAberto(false); onSelecionarTela('planos'); }}
                      style={{
                        width: '100%', background: 'none', border: 'none',
                        color: darkMode ? '#c792ea' : '#667eea',
                        fontWeight: 600, fontSize: 15,
                        padding: '0.55rem 1.1rem',
                        textAlign: 'left', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 10
                      }}
                    >
                      <FaCrown size={14} /> Planos
                    </button>
                  </div>

                  {/* Divisor */}
                  <div style={{ borderTop: `1px solid ${darkMode ? '#2e2e2e' : '#f0f0f0'}` }} />

                  {/* Grupo: Preferências */}
                  <div style={{ padding: '0.4rem 0' }}>
                    <button
                      onClick={() => { setMenuAberto(false); setDarkMode(!darkMode); }}
                      style={{
                        width: '100%', background: 'none', border: 'none',
                        color: darkMode ? '#90caf9' : '#1976d2',
                        fontWeight: 600, fontSize: 15,
                        padding: '0.55rem 1.1rem',
                        textAlign: 'left', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 10
                      }}
                    >
                      {darkMode ? <FaSun size={14} /> : <FaMoon size={14} />}
                      {darkMode ? 'Modo Claro' : 'Modo Escuro'}
                    </button>
                  </div>

                  {/* Divisor */}
                  <div style={{ borderTop: `1px solid ${darkMode ? '#2e2e2e' : '#f0f0f0'}` }} />

                  {/* Grupo: Sessão */}
                  <div style={{ padding: '0.4rem 0' }}>
                    <button
                      onClick={() => { setMenuAberto(false); onLogout(); }}
                      style={{
                        width: '100%', background: 'none', border: 'none',
                        color: darkMode ? '#ef9a9a' : '#d32f2f',
                        fontWeight: 600, fontSize: 15,
                        padding: '0.55rem 1.1rem',
                        textAlign: 'left', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 10
                      }}
                    >
                      <FaSignOutAlt size={14} /> Sair
                    </button>
                  </div>

                </div>
              </>
            )}
        </header>}
      <main className="dashboard-main">
        <section className="dashboard-hero animate-fade-up">
          <div>
            <p className="dashboard-eyebrow">{showGettingStarted ? 'Começo rápido' : 'Painel Inteligente'}</p>
            <h1 className="dashboard-hero-title">{saudacao}, {nomeExibicao}</h1>
            <p className="dashboard-hero-subtitle">
              {showGettingStarted
                ? 'Vamos preparar sua operação para transformar clima e dados de campo em decisões mais seguras.'
                : 'Situação atual da operação, pendências e dados que precisam da sua atenção.'}
            </p>
            <div className="dashboard-hero-actions">
              {showGettingStarted ? (
                <>
                  {canViewOperations && <button className="dashboard-cta-primary" onClick={() => onSelecionarTela('sensoriamento')}>
                    <FaTree /> Abrir Central de Operações
                  </button>}
                  {canViewForecast && <button className="dashboard-cta-secondary" onClick={() => onSelecionarTela('previsao')}>
                    <FaCloudSun /> Consultar previsão
                  </button>}
                </>
              ) : (
                <>
                  {realOperationalDataAvailable && <button className="dashboard-cta-primary" onClick={() => onSelecionarTela('notificacoes-operacionais')}>
                    <FaBell /> Ver pendências
                  </button>}
                  {canViewFieldNotebook && <button className="dashboard-cta-secondary" onClick={() => onOpenFieldNotebook ? onOpenFieldNotebook() : onSelecionarTela('caderno')}>
                    <FaClipboardCheck /> Abrir Caderno
                  </button>}
                </>
              )}
            </div>
          </div>
        </section>

        {showGettingStarted && (
          <section className="dashboard-getting-started animate-fade-up" aria-labelledby="getting-started-title">
            <div className="dashboard-getting-started-heading">
              <div>
                <span className="dashboard-getting-started-kicker">Seu próximo passo</span>
                <h2 id="getting-started-title">Por onde você quer começar?</h2>
              </div>
              <p>Escolha uma opção. Você pode voltar ao painel a qualquer momento.</p>
            </div>

            <div className="dashboard-getting-started-grid">
              {gettingStartedItems.map(item => (
                <article className={`dashboard-start-card dashboard-start-card-${item.accent}`} key={item.key}>
                  <div className="dashboard-start-card-topline">
                    <span className="dashboard-start-card-icon">{item.icon}</span>
                    <span className={`dashboard-start-card-status ${item.completed ? 'is-complete' : ''}`}>
                      {item.completed && <FaCheckCircle aria-hidden="true" />}
                      {item.eyebrow}
                    </span>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <button type="button" onClick={item.onClick} aria-label={`${item.action}: ${item.title}`}>
                    {item.action} <FaArrowRight aria-hidden="true" />
                  </button>
                </article>
              ))}
            </div>
          </section>
        )}

        {readinessCards.length > 0 && (
          <section className="dashboard-readiness" aria-labelledby="dashboard-readiness-title">
            <div className="dashboard-readiness-heading">
              <div><span>Dados reais da conta</span><h2 id="dashboard-readiness-title">Resumo dos módulos disponíveis</h2></div>
            </div>

            <div className="dashboard-readiness-grid">
              {readinessCards.map(card => (
                <button type="button" className={`dashboard-readiness-card is-${card.tone}`} onClick={card.action} key={card.key}>
                  <span className="dashboard-readiness-icon">{card.icon}</span>
                  <span className="dashboard-readiness-copy"><small>{card.label}</small><strong>{card.value}</strong><em>{card.detail}</em></span>
                  <FaArrowRight />
                </button>
              ))}
            </div>

            {realOperationalDataAvailable && <div className="dashboard-operational-layout is-single">
              <article className="dashboard-operational-panel">
                <div className="dashboard-panel-heading">
                  <div><span>Minha rotina</span><h3>Pendências recentes</h3></div>
                  <button type="button" onClick={() => onSelecionarTela('notificacoes-operacionais')}>Ver todas <FaArrowRight /></button>
                </div>
                {operationalNotificationsError ? (
                  <div className="dashboard-panel-empty"><FaExclamationTriangle /><strong>Não foi possível carregar as pendências</strong><span>Tente novamente em alguns instantes.</span></div>
                ) : operationalNotificationsLoading ? (
                  <div className="dashboard-panel-empty"><FaClock /><strong>Carregando pendências</strong><span>Consultando os dados do Caderno de Campo.</span></div>
                ) : dashboardOperationalItems.length > 0 ? (
                  <div className="dashboard-operational-list">
                    {dashboardOperationalItems.map(item => (
                      <button type="button" key={item.id} onClick={() => onSelecionarTela('notificacoes-operacionais')}>
                        <span className={`dashboard-operational-state ${item.readAt ? 'is-read' : ''}`}><FaBell /></span>
                        <span><strong>{item.title}</strong><small>{item.propertyName} · {item.plotName}{(item.orderCode || item.orderId) ? ` · ${item.orderCode || `Ordem ${String(item.orderId).slice(0, 8).toUpperCase()}`}` : ''}</small></span>
                        <time>{formatNotificationTime(item.createdAt)}</time>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="dashboard-panel-empty"><FaCheckCircle /><strong>Nenhuma pendência registrada</strong><span>A rotina operacional está em dia.</span></div>
                )}
              </article>
            </div>}
          </section>
        )}
      </main>
    </div>
  );
}


